// ============================================================
// chatService.js — Cliente del Asesor del Centro de Aplicaciones
// ------------------------------------------------------------
// Llama a la edge function `dimo-asesor` (Supabase, proyecto CRM),
// un agente Claude de SOLO LECTURA que consulta en vivo todo el
// ecosistema Dimoauto (CRM, marketing, Customer360) y responde con
// datos reales. No requiere construir contexto aquí: el agente usa
// sus propias herramientas de lectura.
//
// Flujo:
//   1. Manda los últimos turnos a /functions/v1/dimo-asesor.
//   2. El agente itera (consulta BD → razona) y devuelve la
//      respuesta final completa.
//   3. Se trocea localmente para mantener el efecto typewriter.
//   4. Si la respuesta trae un bloque ```dimo-report {json}``` con
//      datos reales, se extrae y se devuelve como reportSpec para
//      que el ChatPanel genere el PDF.
// ============================================================

const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co';
const SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY';

const ASESOR_URL = `${SUPABASE_URL}/functions/v1/dimo-asesor`;

const COMMON_HEADERS = {
  'Content-Type': 'application/json',
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
};

// ---------- Detección de peticiones de informes (compat) ----------
const REPORT_KEYWORDS = [
  'informe', 'reporte', 'pdf', 'report',
  'genera un informe', 'crea un informe', 'haz un informe',
  'dame un informe', 'descargar informe', 'descarga un informe',
];

export function detectReportRequest(text) {
  const lower = (text || '').toLowerCase();
  if (!REPORT_KEYWORDS.some((k) => lower.includes(k))) return null;
  return { id: 'generico', title: 'Informe Dimoauto' };
}

// ---------- Extracción del bloque de informe ----------
function extractReportSpec(text) {
  const m = (text || '').match(/```dimo-report\s*([\s\S]*?)```/i);
  if (!m) return { clean: text, spec: null };
  let spec = null;
  try {
    spec = JSON.parse(m[1].trim());
  } catch (_e) {
    spec = null;
  }
  const clean = text.replace(m[0], '').trim();
  return { clean, spec };
}

// ---------- Llamada a la edge function dimo-asesor ----------
async function callAsesor(messages, abortSignal) {
  const chatMessages = messages.slice(-8).map((m) => ({
    role: m.role,
    content: m.content,
  }));

  const res = await fetch(ASESOR_URL, {
    method: 'POST',
    headers: COMMON_HEADERS,
    signal: abortSignal,
    body: JSON.stringify({ messages: chatMessages }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `el asesor respondió ${res.status}`);
  }
  const data = await res.json();
  return { content: data.content || '', meta: data.meta || null };
}

// ---------- Typewriter local ----------
async function emitTypewriter(text, onChunk, abortSignal) {
  const tokens = text.match(/(\s+|\S+)/g) || [text];
  for (const tok of tokens) {
    if (abortSignal?.aborted) return;
    onChunk(tok);
    const isPause = /[.!?]\s*$/.test(tok);
    const delay = isPause ? 140 + Math.random() * 90 : 12 + Math.random() * 18;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, delay));
  }
}

// ---------- API pública ----------
export async function sendMessage(messages, onChunk, abortSignal) {
  let respuesta = '';
  let meta = null;
  try {
    const r = await callAsesor(messages, abortSignal);
    respuesta = r.content;
    meta = r.meta;
  } catch (err) {
    if (abortSignal?.aborted) return { reportSpec: null, meta: null, mode: 'aborted' };
    respuesta =
      `⚠️ No pude contactar con el asesor: ${err.message || err}.\n\n` +
      `Si esto persiste, revisa la edge function **dimo-asesor** y el secret ` +
      `**ANTHROPIC_API_KEY** del proyecto Supabase del grupo.`;
  }

  if (!respuesta) respuesta = 'Lo siento, no obtuve respuesta del asesor.';

  const { clean, spec } = extractReportSpec(respuesta);
  const fullText = clean || respuesta;
  await emitTypewriter(fullText, onChunk, abortSignal);
  return { reportSpec: spec, meta, fullText, mode: 'live' };
}
